import React, { useState } from "react";
import { Form } from "@components";
import { Wrapper } from "../wrapper";
import { Box, Link, Grid, Typography } from "@material-ui/core";

export type RemovableMediaExceptionAuthorizationFormValues = {
  employeeName: string;
  position: string;
  employeeID: string;
  networkID: string;
  machineName: string;
  department: string;
  location: string;
  computerModel: string;
  computerType: string;
  reason: string;
  accessType: string; //type of action
  device: string;
  requestType: string;
  permission: string;
  accessDuration: string;
  days: string;
  agreeDoc: boolean;
};

type Key = keyof RemovableMediaExceptionAuthorizationFormValues;

type Props = {
  onCancel: () => void;
  onSubmit: (values: RemovableMediaExceptionAuthorizationFormValues) => void;
  open: boolean;
  values: Partial<RemovableMediaExceptionAuthorizationFormValues>;
  readonly?: boolean;
};

export const RemovableMediaExceptionAuthorizationForm: React.FC<Props> = ({
  onCancel,
  onSubmit,
  open,
  values,
  readonly,
}) => {
  const form = Form.useManager<RemovableMediaExceptionAuthorizationFormValues>({
    employeeName: "",
    position: "",
    employeeID: "",
    networkID: "",
    machineName: "",
    department: "",
    location: "",
    computerModel: "",
    computerType: "",
    reason: "",
    accessType: "", //type of action
    device: "",
    requestType: "",
    permission: "",
    accessDuration: "",
    days: "",
    agreeDoc: false,
    ...values,
  });

  function onFormValueChange(
    name: keyof RemovableMediaExceptionAuthorizationFormValues,
    value: any
  ): void {
    form.set(Form.target.VALUE, [name, value]);

    validate([name, value]).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
  }

  function validate([name, value]: [string, any]): Promise<[Key, Error?]> {
    switch (name) {
      case "employeeName":
      case "position":
      case "networkID":
      case "machineName":
      case "department":
      case "location":
      case "computerModel":
      case "computerType":
      case "reason":
      case "accessType": //type of action
      case "device":
      case "requestType":
      case "permission":
      case "accessDuration":
      case "agreeDoc":
        return Promise.resolve([name as Key, Form.Validators.hasValue(value)]);
      default:
        return Promise.resolve([name as Key]);
    }
  }

  function onFormSubmit(): void {
    Promise.all(Object.entries(form.values).map(validate)).then(
      (validations) => {
        const valid = validations.reduce((valid, [name, error]) => {
          return valid && !error;
        }, true);

        if (valid) {
          onSubmit(form.values);
        } else {
          validations.forEach(([name, error]) => {
            form.set(Form.target.ERROR, [name, error]);
          });
        }
      }
    );
  }

  return (
    <Wrapper
      onCancel={() => {
        onCancel();
        if (values) {
          form.set(Form.target.VALUES, values);
        } else {
          form.reset();
        }
      }}
      onSubmit={onFormSubmit}
      open={open}
      size="md"
      title="Removable Media Exception Authorization Form"
      readonly={readonly}
    >
      <Form>
        <Form.Title>1 - Information</Form.Title>
        <Form.Textbox
          error={form.errors.employeeName}
          label="Employee Name:"
          name="employeeName"
          onChange={onFormValueChange}
          required
          size={6}
          value={form.values.employeeName}
        />
        <Form.Textbox
          error={form.errors.department}
          required
          label="Department Name:"
          name="department"
          onChange={onFormValueChange}
          size={6}
          value={form.values.department}
        />
        <Form.Textbox
          error={form.errors.position}
          required
          label="Position:"
          name="position"
          onChange={onFormValueChange}
          size={6}
          value={form.values.position}
        />
        <Form.Textbox
          error={form.errors.location}
          required
          label="Location / Office / Cube#:"
          name="location"
          onChange={onFormValueChange}
          size={6}
          value={form.values.location}
        />
        <Form.Textbox
          error={form.errors.employeeID}
          label="Employee ID:"
          name="employeeID"
          onChange={onFormValueChange}
          size={6}
          value={form.values.employeeID}
        />
        <Form.Textbox
          error={form.errors.computerModel}
          label="Computer Model:"
          name="computerModel"
          onChange={onFormValueChange}
          required
          size={6}
          value={form.values.computerModel}
        />
        <Form.Textbox
          error={form.errors.networkID}
          label="Network ID:"
          name="networkID"
          onChange={onFormValueChange}
          required
          size={6}
          value={form.values.networkID}
        />
        <Form.Textbox
          error={form.errors.computerType}
          label="Computer Type:"
          name="computerType"
          onChange={onFormValueChange}
          required
          size={6}
          value={form.values.computerType}
        />
        <Form.Textbox
          error={form.errors.machineName}
          label="Machine Name:"
          name="machineName"
          onChange={onFormValueChange}
          required
          size={6}
          value={form.values.machineName}
        />
        <Form.Title subtitle="Please state the full business reason for the request">
          2 - Request Reason
        </Form.Title>
        <Form.Textarea
          error={form.errors.reason}
          label="Justification"
          maxLength={200}
          name="reason"
          onChange={onFormValueChange}
          placeholder="Write details here. Max 200 characters"
          required
          rows={5}
          rowsMax={5}
          size={6}
          value={form.values.reason}
        />
        <Form.Title>3 - Request Information</Form.Title>
        <Form.Dropdown
          error={form.errors.accessType}
          required
          label="Type of Action"
          onChange={onFormValueChange}
          options={["New Access"]}
          name="accessType"
          size={6}
          value={form.values.accessType || ""}
        />
        <Form.Dropdown
          error={form.errors.device}
          required
          label="Device"
          onChange={onFormValueChange}
          options={[
            "Floppy Disk Drive",
            "USB External Storage",
            "CD/DVD Drive",
          ]}
          name="device"
          size={6}
          value={form.values.device || ""}
        />
        <Form.Dropdown
          error={form.errors.accessDuration}
          required
          label="Access Duration"
          onChange={onFormValueChange}
          options={["Permanent", "Temporary"]}
          name="accessDuration"
          size={6}
          value={form.values.accessDuration || ""}
        />
        <Form.Dropdown
          error={form.errors.requestType}
          required
          label="Request Type"
          onChange={onFormValueChange}
          options={["Add", "Removed"]}
          name="requestType"
          size={6}
          value={form.values.requestType || ""}
        />
        <Form.Textbox
          label="How many days?"
          name="days"
          onChange={onFormValueChange}
          size={6}
          value={form.values.days}
        />
        <Form.Dropdown
          error={form.errors.permission}
          required
          label={{
            text: "Permission",
            tooltip:
              "1. Full Access - Copy, Move, Open, Save, Delete, Execute.\n2. Modify - Copy, Move, Open, Save, Delete.\n3. Read and Execute - Copy, Open, Execute.\n4. Read - Copy, Open.\n5. List device content only - View the content of the device.",
          }}
          onChange={onFormValueChange}
          options={[
            "Full",
            "Modify",
            "Read and Execute",
            "Read",
            "List device content only",
          ]}
          name="permission"
          size={6}
          value={form.values.permission || ""}
        />
        <Grid item xs={12}>
          <Form.Error error={form.errors.agreeDoc} />
        </Grid>
        <Grid item xs={12}>
          <Box
            borderColor="primary.main"
            border={0}
            borderLeft={5}
            bgcolor="grey.200"
            p={1}
            display="flex"
          >
            <Box mt={-1} mr={-1}>
              <Form.Checkbox
                name="agreeDoc"
                onChange={onFormValueChange}
                size={12}
                value={form.values.agreeDoc}
              />
            </Box>
            <Box ml={-1} flexGrow={1}>
              <Typography variant="caption">
                I have read and understand our policies and standards regarding
                the use of removable media, and agree to appropriately secure
                such media and appropriately encrypt data stored on such media,
                as set forth in the{" "}
                <Link
                  href="https://drive.google.com/open?id=1X_0VmR0bu8Zf_etoOsQhHBDXST7B9pBT"
                  target="_blank"
                >
                  Information Classification &amp; Handling Standard (IC&amp;H
                  Standard)
                </Link>
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Form>
    </Wrapper>
  );
};
